import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchProjects } from '../services/api';
import { localizedProject } from '../utils/localizedContent';

const projectGradients = {
    'IoT Gateway': { color1: '#a8aba2', color2: '#6c60ff' },
    'Workout Program Builder': { color1: '#9f9ec0', color2: '#88bac3' },
    'Order Management System': { color1: '#dc2626', color2: '#000000' },
    'Document Management System': { color1: '#b29053', color2: '#000000' }
};

function Projects({ isDarkMode }) {
    const { t, i18n } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [currentMobileIndex, setCurrentMobileIndex] = useState(0);

    useEffect(() => {
        const getProjects = async () => {
            try{
                const data = await fetchProjects();
                setProjects(data);
                // Set the first project as selected by default
                if (data.length > 0) {
                    setSelectedProject(data[0]);
                    setCurrentMobileIndex(0);
                }
            }catch (error){
                console.error("Error fetching projects: ", error)
            }finally{
                setLoading(false);
            }
        };
        getProjects();
    }, []);

    const handleProjectSelect = useCallback((project) => {
        setSelectedProject(project);
    }, []);

    const handleProjectClick = useCallback((project) => {
        if (project && project.repositoryUrl) {
            window.open(project.repositoryUrl, '_blank', 'noopener,noreferrer');
        }
    }, []);

    const handleImageError = useCallback((e) => {
        e.target.style.display = 'none';
    }, []);

    // Mobile navigation functions
    const handlePrevProject = useCallback(() => {
        setCurrentMobileIndex((prev) => {
            const newIndex = prev === 0 ? projects.length - 1 : prev - 1;
            if (projects[newIndex]) {
                setSelectedProject(projects[newIndex]);
            }
            return newIndex;
        });
    }, [projects]);

    const handleNextProject = useCallback(() => {
        setCurrentMobileIndex((prev) => {
            const newIndex = prev === projects.length - 1 ? 0 : prev + 1;
            if (projects[newIndex]) {
                setSelectedProject(projects[newIndex]);
            }
            return newIndex;
        });
    }, [projects]);

    const displayProjects = useMemo(
        () => projects.map((project) => localizedProject(project, i18n.language)),
        [projects, i18n.language]
    );

    const displaySelectedProject = useMemo(() => {
        if (!selectedProject) return null;
        return localizedProject(selectedProject, i18n.language);
    }, [selectedProject, i18n.language]);

    const getProjectGradient = useCallback((project) => {
        const key = project?.gradientKey || project?.name;
        return key in projectGradients ? projectGradients[key] : null;
    }, []);

    if (loading){
        return (
            <div className="min-h-screen flex items-center justify-center transition-colors duration-300">
                <div className="text-center">
                    <div className={`w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4 transition-colors duration-300 ${
                        isDarkMode ? 'border-purple-500' : 'border-blue-500'
                    }`}></div>
                    <p className={`text-lg transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                        {t('projects.loading')}
                    </p>
                </div>
            </div>
        );
    }

    return(
        <section className={`min-h-screen pt-4 pb-4 lg:py-16 px-4 sm:px-6 lg:px-8 transition-colors duration-300`} id="projects">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6 lg:mb-16">
                    <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 lg:mb-6 transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                        {t('projects.title')}
                    </h1>
                    <p className={`text-sm sm:text-base lg:text-xl max-w-2xl mx-auto transition-colors duration-300 mb-4 lg:mb-0 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                        {t('projects.subtitle')}
                    </p>
                </div>
                
                {/* Main Content Area */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Panel - Project Selection (Hidden on mobile) */}
                    <div className="hidden lg:block space-y-6">
                        <h2 className={`text-2xl font-bold mb-6 transition-colors duration-300 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                            {t('projects.selectProject')}
                        </h2>
                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2" id="projects-list-col">
                            {projects.length > 0 ? (
                                displayProjects.map((project) => (
                                    <div
                                        key={project.id}
                                        className={`group flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-300 border ${
                                            selectedProject?.id === project.id
                                                ? isDarkMode 
                                                    ? 'bg-gradient-to-r from-purple-600/20 to-purple-500/10 border-purple-400/60 shadow-lg shadow-purple-500/25'
                                                    : 'bg-gradient-to-r from-blue-600/20 to-blue-500/10 border-blue-400/60 shadow-lg shadow-blue-500/25'
                                                : isDarkMode
                                                    ? 'bg-gradient-to-br from-gray-800/60 to-gray-900/80 border-gray-700/50 hover:border-purple-400/50 hover:bg-gray-800/70 hover:shadow-lg hover:shadow-purple-500/10'
                                                    : 'bg-gradient-to-br from-white/90 to-gray-50/95 border-gray-200/80 hover:border-blue-400/50 hover:bg-white/95 hover:shadow-lg hover:shadow-blue-500/10'
                                        } backdrop-blur-sm`}
                                        onClick={() => {
                                            const raw = projects.find((p) => p.id === project.id);
                                            handleProjectSelect(raw || project);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleProjectSelect(project);
                                            }
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        aria-label={t('projects.selectProjectAria', { name: project.name })}
                                    >
                                        <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                                            <img
                                                src={project.imageUrl}
                                                alt={t('projects.screenshotAlt', { name: project.name })}
                                                className="w-full h-full object-cover"
                                                onError={handleImageError}
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className={`font-semibold text-lg mb-1 transition-colors duration-300 ${
                                                isDarkMode ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {project.name}
                                            </h3>
                                            <p className={`text-sm transition-colors duration-300 ${
                                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                {project.organization}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className={`text-center py-8 transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    {t('projects.noProjects')}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Desktop Panel - Selected Project Display */}
                    <div className="hidden lg:block space-y-6">
                        <h2 className={`text-2xl font-bold mb-6 transition-colors duration-300 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                            {t('projects.projectDetails')}
                        </h2>
                        <div className="flex">
                        {displaySelectedProject ? (
                            <div className={`relative rounded-3xl overflow-hidden shadow-2xl w-full h-[500px] transition-all duration-300 backdrop-blur-sm group`}
                                style={{
                                    background: getProjectGradient(displaySelectedProject)
                                        ? `linear-gradient(135deg, ${getProjectGradient(displaySelectedProject).color1}, ${getProjectGradient(displaySelectedProject).color2})`
                                        : isDarkMode
                                            ? 'linear-gradient(to bottom right, rgb(17 24 39 / 0.95), rgb(31 41 55 / 0.9))'
                                            : 'linear-gradient(to bottom right, rgb(255 255 255 / 0.95), rgb(249 250 251 / 0.9))'
                                }}>
                                {/* Animated Border Glow */}
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"></div>
                                <div className="absolute inset-[1px] rounded-3xl border border-white/10 pointer-events-none"></div>
                                
                                {/* Subtle Pattern Overlay */}
                                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
                                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                                    backgroundSize: '40px 40px'
                                }}></div>

                                {/* Project Image - Top Right with Glow Effect */}
                                <div className="absolute top-6 right-6 z-20">
                                    <div className="w-28 h-28 rounded-xl overflow-hidden border-2 border-white/30 shadow-2xl relative group-hover:scale-105 transition-all duration-300">
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none"></div>
                                        <img
                                            src={displaySelectedProject.imageUrl}
                                            alt={t('projects.projectAlt', { name: displaySelectedProject.name })}
                                            className="w-full h-full object-cover"
                                            onError={handleImageError}
                                        />
                                    </div>
                                </div>
                                
                                {/* Tech Stack Icons - Bottom Left with Glassmorphism */}
                                <div className="absolute bottom-4 left-4 z-20 flex gap-2 flex-wrap">
                                    {(() => {
                                        let techStackArray = null;
                                        if (displaySelectedProject.techStack) {
                                            if (typeof displaySelectedProject.techStack === 'string') {
                                                try {
                                                    techStackArray = JSON.parse(displaySelectedProject.techStack);
                                                } catch {
                                                    techStackArray = displaySelectedProject.techStack.split(',').map(tech => tech.trim());
                                                }
                                            } else if (Array.isArray(displaySelectedProject.techStack)) {
                                                techStackArray = displaySelectedProject.techStack;
                                            }
                                        }
                                        return techStackArray && techStackArray.length > 0 ? techStackArray.map((tech, index) => (
                                            <div key={index} className={`p-2 rounded-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${
                                                isDarkMode 
                                                    ? 'bg-white/10 border border-white/20 shadow-lg hover:bg-white/15 hover:shadow-white/10' 
                                                    : 'bg-white/30 border border-white/40 shadow-lg hover:bg-white/40 hover:shadow-white/20'
                                            }`}>
                                                <img
                                                    src={tech}
                                                    alt={t('projects.techStackAlt', { index: index + 1 })}
                                                    className="w-7 h-7 object-contain"
                                                    onError={handleImageError}
                                                    loading="lazy"
                                                />
                                            </div>
                                        )) : null;
                                    })()}
                                </div>
                                
                                {/* Project Content */}
                                <div className="relative z-10 p-8 h-full flex flex-col">
                                    {/* Header */}
                                    <div className="mb-6 pr-24">
                                        <h2 className={`text-3xl lg:text-4xl font-extrabold mb-2 transition-colors duration-300`} style={{
                                            color: 'white',
                                            textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}>
                                            {displaySelectedProject.name}
                                        </h2>
                                        <p className={`text-lg font-medium transition-colors duration-300`} style={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            textShadow: '0 1px 4px rgba(0,0,0,0.2)'
                                        }}>
                                            {displaySelectedProject.organization}
                                        </p>
                                    </div>
                                    
                                    {/* Description */}
                                    <div className="flex-1 overflow-y-auto pr-2">
                                        <p className={`leading-relaxed text-base lg:text-lg transition-colors duration-300`} style={{
                                            color: 'rgba(255, 255, 255, 0.95)',
                                            textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}>
                                            {displaySelectedProject.description}
                                        </p>
                                    </div>
                                    
                                    {/* GitHub Button - Bottom Right with Glassmorphism */}
                                    <div className="flex items-end justify-end mt-6">
                                        <button 
                                            className={`p-3 rounded-xl backdrop-blur-md transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${
                                                isDarkMode 
                                                    ? 'bg-white/15 hover:bg-white/20 text-white border border-white/30 shadow-2xl hover:shadow-white/20' 
                                                    : 'bg-white/20 hover:bg-white/25 text-white border border-white/30 shadow-2xl hover:shadow-white/20'
                                            }`}
                                            onClick={() => handleProjectClick(selectedProject)}
                                            title={t('projects.viewRepository')}
                                        >
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={`flex items-center justify-center h-[500px] rounded-2xl border-2 border-dashed transition-colors duration-300 ${
                                isDarkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
                            }`}>
                                <p className="text-lg">{t('projects.selectToView')}</p>
                            </div>
                        )}
                        </div>
                    </div>

                    {/* Mobile Panel - Carousel View */}
                    <div className="lg:hidden w-full max-w-sm mx-auto">
                        {displaySelectedProject ? (
                            <div className={`relative rounded-3xl overflow-hidden shadow-2xl w-full transition-all duration-500 backdrop-blur-sm`}
                                style={{
                                    background: getProjectGradient(displaySelectedProject)
                                        ? `linear-gradient(180deg, ${getProjectGradient(displaySelectedProject).color1} 0%, ${getProjectGradient(displaySelectedProject).color2} 100%)`
                                        : isDarkMode
                                            ? 'linear-gradient(180deg, rgb(17 24 39) 0%, rgb(31 41 55) 100%)'
                                            : 'linear-gradient(180deg, rgb(243 244 246) 0%, rgb(229 231 235) 100%)'
                                }}>
                                {/* Subtle Pattern Overlay */}
                                <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{
                                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                                    backgroundSize: '40px 40px'
                                }}></div>
                                
                                {/* Enhanced Border Glow */}
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/15 via-transparent to-transparent pointer-events-none"></div>
                                <div className="absolute inset-[1px] rounded-3xl border border-white/20 pointer-events-none"></div>
                                
                                {/* Navigation Arrows - Top Right */}
                                <div className="absolute top-4 right-4 z-30 flex gap-2">
                                    <button
                                        onClick={handlePrevProject}
                                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full backdrop-blur-lg flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 ${
                                            isDarkMode
                                                ? 'bg-white/25 hover:bg-white/35 text-white border border-white/40 shadow-xl backdrop-saturate-150'
                                                : 'bg-white/40 hover:bg-white/50 text-gray-800 border border-white/50 shadow-xl backdrop-saturate-150'
                                        }`}
                                        aria-label={t('projects.previousProject')}
                                    >
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={handleNextProject}
                                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full backdrop-blur-lg flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 ${
                                            isDarkMode
                                                ? 'bg-white/25 hover:bg-white/35 text-white border border-white/40 shadow-xl backdrop-saturate-150'
                                                : 'bg-white/40 hover:bg-white/50 text-gray-800 border border-white/50 shadow-xl backdrop-saturate-150'
                                        }`}
                                        aria-label={t('projects.nextProject')}
                                    >
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Project Content */}
                                <div className="relative z-10 p-5 sm:p-6 h-full flex flex-col min-h-[500px] sm:min-h-[550px]">
                                    {/* Project Name */}
                                    <div className="mb-4 pr-20 sm:pr-24">
                                        <h2 className={`text-2xl sm:text-3xl font-extrabold leading-tight transition-colors duration-300`} style={{
                                            color: 'white',
                                            textShadow: '0 3px 12px rgba(0,0,0,0.4)',
                                            letterSpacing: '-0.02em'
                                        }}>
                                            {displaySelectedProject.name}
                                        </h2>
                                    </div>

                                    {/* Description */}
                                    <div className="mb-6 flex-1">
                                        <p className={`leading-relaxed text-sm sm:text-base transition-colors duration-300`} style={{
                                            color: 'rgba(255, 255, 255, 0.95)',
                                            textShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                            lineHeight: '1.6'
                                        }}>
                                            {displaySelectedProject.description}
                                        </p>
                                    </div>

                                    {/* Bottom Section - Tech Stack and Mockup */}
                                    <div className="flex items-start justify-between gap-4 sm:gap-5 mt-auto">
                                        {/* Tech Stack Icons - Bottom Left (Vertical Stack) */}
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                            {(() => {
                                                let techStackArray = null;
                                                if (displaySelectedProject.techStack) {
                                                    if (typeof displaySelectedProject.techStack === 'string') {
                                                        try {
                                                            techStackArray = JSON.parse(displaySelectedProject.techStack);
                                                        } catch {
                                                            techStackArray = displaySelectedProject.techStack.split(',').map(tech => tech.trim());
                                                        }
                                                    } else if (Array.isArray(displaySelectedProject.techStack)) {
                                                        techStackArray = displaySelectedProject.techStack;
                                                    }
                                                }
                                                return techStackArray && techStackArray.length > 0 ? techStackArray.slice(0, 5).map((tech, index) => (
                                                    <div key={index} className={`p-2 rounded-lg backdrop-blur-lg transition-all duration-300 hover:scale-105 ${
                                                        isDarkMode 
                                                            ? 'bg-white/15 border border-white/30 shadow-lg hover:bg-white/20 hover:shadow-xl' 
                                                            : 'bg-white/35 border border-white/50 shadow-lg hover:bg-white/45 hover:shadow-xl'
                                                    }`}>
                                                        <img
                                                            src={tech}
                                                            alt={t('projects.techStackAlt', { index: index + 1 })}
                                                            className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
                                                            onError={handleImageError}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                )) : null;
                                            })()}
                                        </div>

                                        {/* Mockup Image - Bottom Right */}
                                        <div className="flex-shrink-0">
                                            <img
                                                src={displaySelectedProject.imageUrl}
                                                alt={t('projects.projectMockupAlt', { name: displaySelectedProject.name })}
                                                className="w-52 h-52 sm:w-60 sm:h-60 object-contain drop-shadow-2xl"
                                                style={{
                                                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))'
                                                }}
                                                onError={handleImageError}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={`flex items-center justify-center min-h-[600px] rounded-2xl border-2 border-dashed transition-colors duration-300 ${
                                isDarkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
                            }`}>
                                <p className="text-lg">{t('projects.noProjectsMobile')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Projects;